import Breadcrumb from "components/Common/Breadcrumb"
import { useEffect, useMemo, useState } from "react"
import { FaCrown, FaEdit, FaPlus, FaSave } from "react-icons/fa"
import { MdCancel, MdDelete } from "react-icons/md"
import {
  Badge,
  Button,
  Card,
  CardBody,
  FormFeedback,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Table,
} from "reactstrap"
import React from "react"
import Loader from "components/Loader/Loader"
import { usePermissionData } from "AppContext"
import {
  fetchData,
  FetchDeleteData,
  FetchPostData,
  FetchPutData,
} from "utils/Common_api_functions"
import Swal from "sweetalert2"
import moment from "moment"
import { Switch } from "antd"
import Select from "react-select"

const EMPTY_FORM = { departmentName: "", employees: [], headId: null }

// Solid backgrounds for the employees dropdown (app CSS leaves it transparent)
const EMPLOYEE_SELECT_STYLES = {
  control: provided => ({
    ...provided,
    backgroundColor: "#ffffff",
    borderRadius: 16,
    minHeight: 38,
  }),
  menu: provided => ({
    ...provided,
    backgroundColor: "#ffffff",
  }),
  menuList: provided => ({
    ...provided,
    backgroundColor: "#ffffff",
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected
      ? "#cb9019"
      : state.isFocused
        ? "#faf3e2"
        : "#ffffff",
    color: state.isSelected ? "#ffffff" : "#263a5b",
  }),
  multiValue: provided => ({
    ...provided,
    backgroundColor: "#faf3e2",
  }),
  multiValueLabel: provided => ({
    ...provided,
    color: "#6b5a33",
  }),
}

// Employees should be an array of ids, but tolerate a JSON string from the API
const parseEmployees = value => {
  let list = value
  if (typeof list === "string") {
    try {
      list = JSON.parse(list)
    } catch (e) {
      return []
    }
  }
  return Array.isArray(list) ? list : []
}

const Departments = () => {
  document.title = "Departments | Vintage Infra Equipment Sol PVT LTD"
  const [data, setData] = useState([])
  const [staff, setStaff] = useState([])
  const [loading, setLoading] = useState(false)
  const [pageSize, setPageSize] = useState(20)
  const [searchString, setSearchString] = useState("")

  // Pagination
  const [currentPage, setCurrentPage] = useState(1)

  const [modalOpen, setModalOpen] = useState(false)
  const [selectedDepartment, setSelectedDepartment] = useState(EMPTY_FORM)
  const [errors, setErrors] = useState({})

  const { checkAccess } = usePermissionData()

  useEffect(() => {
    getDepartments()
    getEmployees()
  }, [])

  async function getDepartments() {
    const APIData = await fetchData(setLoading, "departments")
    if (APIData?.success) {
      setData(
        (APIData?.data || []).map(dept => ({
          ...dept,
          employees: parseEmployees(dept.employees),
        }))
      )
    }
  }

  async function getEmployees() {
    const APIData = await fetchData(setLoading, "users")
    if (APIData?.success) {
      setStaff(APIData?.data.filter(user => user.status))
    }
  }

  const employeeOptions = useMemo(
    () =>
      staff.map(emp => ({ value: emp.id, label: emp.name })),
    [staff]
  )

  const getEmployeeName = id =>
    staff.find(emp => emp.id === id)?.name || "-"

  // Head is mandatory. With a single employee, that employee is the head.
  const resolveHeadId = item => {
    const employees = item?.employees || []
    if (employees.length === 1) return employees[0]
    return employees.includes(item?.headId) ? item?.headId : null
  }

  // Head can only be picked from the employees selected for this department
  const headOptions = useMemo(
    () =>
      (selectedDepartment?.employees || [])
        .map(id => staff.find(emp => emp.id === id))
        .filter(Boolean)
        .map(emp => ({ value: emp.id, label: emp.name })),
    [selectedDepartment?.employees, staff]
  )

  // Single-staff department: that staff member is the head automatically
  const autoAssignedHead =
    (selectedDepartment?.employees?.length || 0) === 1 &&
    !!selectedDepartment?.headId

  const filteredData = useMemo(() => {
    const search = searchString.toLowerCase()
    return data.filter(
      item =>
        item?.departmentName?.toLowerCase().includes(search) ||
        (!!resolveHeadId(item) &&
          getEmployeeName(resolveHeadId(item)).toLowerCase().includes(search)) ||
        item?.employees?.some(id =>
          getEmployeeName(id).toLowerCase().includes(search)
        )
    )
  }, [data, searchString, staff])

  const totalPages = Math.ceil(filteredData.length / pageSize) || 1
  const start = (currentPage - 1) * pageSize

  const paginatedData = useMemo(() => {
    const end = start + pageSize
    return filteredData.slice(start, end)
  }, [filteredData, currentPage, pageSize, start])

  const openAddModal = () => {
    setSelectedDepartment(EMPTY_FORM)
    setErrors({})
    setModalOpen(true)
  }

  const openEditModal = item => {
    const employees = [...(item.employees || [])]
    setSelectedDepartment({
      ...item,
      employees,
      headId: resolveHeadId({ ...item, employees }),
    })
    setErrors({})
    setModalOpen(true)
  }

  const validate = () => {
    const newErrors = {}
    if (!selectedDepartment?.departmentName?.trim()) {
      newErrors.departmentName = "Department name is required"
    }
    if (!selectedDepartment?.employees?.length) {
      newErrors.employees = "Select at least one employee"
    }
    if (
      selectedDepartment?.employees?.length &&
      !resolveHeadId(selectedDepartment)
    ) {
      newErrors.headId = "Department head is mandatory"
    }
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async () => {
    if (!validate()) return

    const payload = {
      departmentName: selectedDepartment.departmentName.trim(),
      employees: selectedDepartment.employees,
      headId: resolveHeadId(selectedDepartment),
    }
    const isEdit = !!selectedDepartment?.id

    const APIdata = isEdit
      ? await FetchPutData(
          setLoading,
          "departments",
          selectedDepartment.id,
          payload
        )
      : await FetchPostData(setLoading, "departments", payload)

    if (APIdata?.data?.success) {
      Swal.fire({
        title: `Department ${isEdit ? "Updated" : "Added"} Successfully.`,
        text: APIdata?.data?.message,
        icon: "success",
      })
      setModalOpen(false)
      setSelectedDepartment(EMPTY_FORM)
      setCurrentPage(1)
      getDepartments()
    } else {
      Swal.fire({
        title: `Error ${isEdit ? "Updating" : "Adding"} Department.`,
        text: APIdata?.response?.data?.message,
        icon: "error",
      })
    }
  }

  const handleToggleActive = async item => {
    const APIdata = await FetchPutData(setLoading, "departments", item.id, {
      isActive: !item.isActive,
    })
    if (APIdata?.data?.success) {
      getDepartments()
    } else {
      Swal.fire({
        title: "Error Updating Department.",
        text: APIdata?.response?.data?.message,
        icon: "error",
      })
    }
  }

  const handleDelete = item => {
    Swal.fire({
      title: `Are you sure you want to delete "${item?.departmentName}"?`,
      text: "This action cannot be undone!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
    }).then(async result => {
      if (result.isConfirmed) {
        const APIdata = await FetchDeleteData(setLoading, "departments", item.id)
        if (APIdata?.data?.success) {
          Swal.fire({
            title: "Department Deleted Successfully.",
            text: APIdata?.data?.message,
            icon: "success",
          })
          getDepartments()
        } else {
          Swal.fire({
            title: "Error Deleting Department.",
            text: APIdata?.response?.data?.message,
            icon: "error",
          })
        }
      }
    })
  }
  const renderHead = headId => {
    if (!headId) return <span className="text-muted">Not assigned</span>
    return (
      <Badge pill color="warning" className="fw-normal text-dark fs-6">
        <FaCrown className="me-1" size={12} />
        {getEmployeeName(headId)}
      </Badge>
    )
  }

  const renderEmployees = ids => {
    if (!ids?.length) return <span className="text-muted">-</span>
    return (
      <div className="d-flex flex-wrap gap-1">
        {ids.map(id => (
          <Badge key={id} pill color="primary" className="fw-normal fs-6">
            {getEmployeeName(id)}
          </Badge>
        ))}
      </div>
    )
  }

  return (
    <div className="page-content">
      {loading && <Loader />}
      <Breadcrumb title="Dashboard" breadcrumbItem="Departments" />

      <Card className="shadow-lg">
        <CardBody>
          {/* Top controls */}
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div className="d-flex gap-3">
              <Input
                type="select"
                value={pageSize}
                onChange={e => {
                  setPageSize(Number(e.target.value)), setCurrentPage(1)
                }}
                className="rounded-4 w-auto pe-5"
              >
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </Input>

              <Input
                className="rounded-4"
                placeholder="Search Departments"
                value={searchString}
                onChange={e => {
                  setSearchString(e.target.value)
                  setCurrentPage(1)
                }}
              />
            </div>
            {checkAccess(17, "add") && (
              <Button className="btn-blue rounded-4 px-3" onClick={openAddModal}>
                <FaPlus className="me-2" />
                Department
              </Button>
            )}
          </div>

          {/* Table */}
          <Table bordered responsive>
            <thead className="table-dark">
              <tr>
                <th style={{ width: "60px" }}>#</th>
                <th>Department Name</th>
                <th>Employees</th>
                <th style={{ width: "170px" }}>Department Head</th>
                <th style={{ width: "120px" }}>Status</th>
                <th>Created At</th>
                <th className="text-center" style={{ width: "140px" }}>
                  Action
                </th>
              </tr>
            </thead>

            <tbody>
              {paginatedData.length === 0 ? (
                <tr>
                  <td colSpan="7" className="text-center">
                    No Departments found
                  </td>
                </tr>
              ) : (
                paginatedData.map((item, index) => (
                  <tr key={item.id}>
                    <td>{start + index + 1}</td>

                    <td>{item.departmentName}</td>
                    <td>{renderEmployees(item.employees)}</td>
                    <td>{renderHead(resolveHeadId(item))}</td>
                    <td>
                      <Switch
                        checked={item.isActive}
                        disabled={!checkAccess(17, "edit")}
                        onChange={() => handleToggleActive(item)}
                      />
                    </td>
                    <td>
                      {moment(item.createdAt).format("DD-MM-YYYY | hh:mm A")}
                    </td>

                    <td className="text-center" style={{ whiteSpace: "nowrap" }}>
                      {checkAccess(17, "edit") && (
                        <Button
                          className="bg-success text-white me-2 border-0 shadow-none"
                          onClick={() => openEditModal(item)}
                        >
                          <FaEdit size={16} />
                        </Button>
                      )}

                      {checkAccess(17, "delete") && (
                        <Button
                          className="bg-danger text-white border-0 shadow-none"
                          onClick={() => handleDelete(item)}
                        >
                          <MdDelete size={18} />
                        </Button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
          <nav
            aria-label="Page navigation example"
            className="d-flex justify-content-between mt-3"
          >
            <h5>
              Showing {filteredData.length === 0 ? 0 : start + 1} to{" "}
              {Math.min(start + pageSize, filteredData.length)} of{" "}
              {filteredData.length} entries
            </h5>
            <ul className="pagination mb-0">
              <li
                className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
              >
                <button
                  className="page-link"
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                >
                  Previous
                </button>
              </li>

              {[...Array(totalPages)].map((_, i) => {
                const pageNum = i + 1
                return (
                  <li
                    key={pageNum}
                    className={`page-item ${
                      pageNum === currentPage ? "active" : ""
                    }`}
                  >
                    <button
                      className="page-link"
                      onClick={() => setCurrentPage(pageNum)}
                    >
                      {pageNum}
                    </button>
                  </li>
                )
              })}

              <li
                className={`page-item ${
                  currentPage === totalPages ? "disabled" : ""
                }`}
              >
                <button
                  className="page-link"
                  onClick={() =>
                    setCurrentPage(prev => Math.min(prev + 1, totalPages))
                  }
                >
                  Next
                </button>
              </li>
            </ul>
          </nav>
        </CardBody>
      </Card>

      <Modal isOpen={modalOpen} toggle={() => setModalOpen(false)} centered>
        <ModalHeader toggle={() => setModalOpen(false)}>
          {selectedDepartment?.id ? "Edit" : "Add"} Department
        </ModalHeader>
        <ModalBody>
          <div className="mb-3">
            <Label>
              Department Name <span className="text-danger">*</span>
            </Label>
            <Input
              placeholder="e.g. Human Resources"
              value={selectedDepartment?.departmentName || ""}
              invalid={!!errors.departmentName}
              onChange={e =>
                setSelectedDepartment(prev => ({
                  ...prev,
                  departmentName: e.target.value,
                }))
              }
            />
            {errors.departmentName && (
              <FormFeedback className="d-block">
                {errors.departmentName}
              </FormFeedback>
            )}
          </div>

          <div>
            <Label>
              Employees <span className="text-danger">*</span>
            </Label>
            <Select
              isMulti
              options={employeeOptions}
              placeholder="Select employees"
              styles={EMPLOYEE_SELECT_STYLES}
              value={employeeOptions.filter(opt =>
                selectedDepartment?.employees?.includes(opt.value)
              )}
              onChange={selected => {
                const employees = (selected || []).map(opt => opt.value)
                setSelectedDepartment(prev => ({
                  ...prev,
                  employees,
                  // Auto-assign the only staff member / drop a removed head
                  headId: resolveHeadId({ employees, headId: prev.headId }),
                }))
              }}
              className={errors.employees ? "is-invalid" : ""}
            />
            {errors.employees && (
              <div className="invalid-feedback d-block">
                {errors.employees}
              </div>
            )}
          </div>

          <div className="mt-3">
            <Label>
              Department Head <span className="text-danger">*</span>
            </Label>
            <Select
              options={headOptions}
              placeholder={
                headOptions.length
                  ? "Select department head"
                  : "Select employees first"
              }
              isDisabled={!headOptions.length || autoAssignedHead}
              styles={EMPLOYEE_SELECT_STYLES}
              value={
                headOptions.find(opt => opt.value === selectedDepartment?.headId) ||
                null
              }
              onChange={selected =>
                setSelectedDepartment(prev => ({
                  ...prev,
                  headId: selected?.value ?? null,
                }))
              }
              className={errors.headId ? "is-invalid" : ""}
            />
            {errors.headId ? (
              <div className="invalid-feedback d-block">{errors.headId}</div>
            ) : autoAssignedHead ? (
              <small className="text-muted">
                Only one staff member — they are assigned as the head
                automatically.
              </small>
            ) : (
              <small className="text-muted">
                Head must be one of the selected employees.
              </small>
            )}
          </div>
        </ModalBody>
        <ModalFooter>
          <Button className="btn-blue" onClick={handleSubmit}>
            <FaSave /> Save
          </Button>
          <Button className="btn-danger" onClick={() => setModalOpen(false)}>
            <MdCancel /> Cancel
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  )
}

export default Departments
